<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleReset">重置</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else><i class="ri-save-line"></i> 儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <!-- Tab 導航 -->
    <div class="tab-nav">
      <button class="tab-btn" :class="{ active: activeTab === 'basic' }" @click="activeTab = 'basic'">
        <i class="ri-settings-3-line"></i> 基本設定
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'rewards' }" @click="activeTab = 'rewards'">
        <i class="ri-coupon-line"></i> 點數 &amp; 優惠券
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'notification' }" @click="activeTab = 'notification'">
        <i class="ri-notification-3-line"></i> 通知設定
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <template v-else>
      <!-- ======== Tab 1：基本設定 ======== -->
      <div v-show="activeTab === 'basic'" class="card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">
            <div class="form-section">
              <h2 class="section-title">基本設定</h2>
              <div
                v-if="isWixtarClient"
                class="wixtar-tier-upgrade-notice"
              >
                <i class="ri-information-line"></i>
                <div>
                  <p>
                    Wixtar 會員模式預設不會因交易自動升等或發送升等禮。
                    若要以 Wixtar POS／Lockline 商城交易觸發（商城單也會先到 Wixtar，再經 webhook 訂單事件），請至
                    <router-link to="/client/settings?tab=wixtar">系統參數 → Wixtar 整合</router-link>
                    開啟「Wixtar 交易觸發會員升等」。
                  </p>
                  <p v-if="wixtarTransactionTierUpgradeEnabled" class="notice-on">
                    目前已開啟：POS 與 Lockline 商城訂單在已完成且已付款時會重算等級，升等後可發送本頁設定的升等禮。
                  </p>
                  <p v-else class="notice-off">
                    目前為關閉：POS／Lockline 商城交易都不會觸發升等禮；Wixtar VIP 群組同步改等級也不會發升等禮。
                  </p>
                </div>
              </div>
              <div class="form-group">
                <div class="toggle-row">
                  <label class="toggle-label">啟用升等禮自動發送</label>
                  <label class="toggle-switch">
                    <input v-model="form.isEnabled" type="checkbox" />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-status" :class="form.isEnabled ? 'on' : 'off'">
                    {{ form.isEnabled ? '已啟用' : '已停用' }}
                  </span>
                </div>
              </div>
              <div class="form-group">
                <div class="toggle-row">
                  <label class="toggle-label">僅首次升等發送</label>
                  <label class="toggle-switch">
                    <input v-model="form.onlyFirstUpgrade" type="checkbox" />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-status" :class="form.onlyFirstUpgrade ? 'on' : 'off'">
                    {{ form.onlyFirstUpgrade ? '已啟用' : '已停用' }}
                  </span>
                </div>
                <small class="form-text" style="margin-top:8px;">啟用後，同一等級的多次升降級僅在首次升等時發送</small>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- ======== Tab 2：點數 & 優惠券 ======== -->
      <div v-show="activeTab === 'rewards'" class="card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">
            <div class="form-section">
              <h2 class="section-title">依目標等級設定</h2>
              <p class="section-description">為不同的目標等級設定不同的升等禮內容</p>

              <div v-for="(tierConfig, index) in form.tierConfigs" :key="index" class="tier-config-card">
                <div class="tier-config-header">
                  <div class="tier-config-title">
                    <i class="ri-medal-line"></i>
                    <span>{{ getTierName(tierConfig.targetTier) }}</span>
                  </div>
                </div>

                <div class="tier-config-body">

                  <!-- 點數設定 -->
                  <div class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送點數</label>
                      <label class="toggle-switch">
                        <input v-model="tierConfig.sendPoints" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="tierConfig.sendPoints ? 'on' : 'off'">
                        {{ tierConfig.sendPoints ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="tierConfig.sendPoints" class="form-group">
                    <label class="form-label">點數數量 <span class="required">*</span></label>
                    <input
                      v-model.number="tierConfig.pointsAmount"
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': errors[`tierConfigs.${index}.pointsAmount`] }"
                      placeholder="請輸入大於 0 的點數"
                      min="1"
                      @focus="$event.target.select()"
                    />
                    <div v-if="errors[`tierConfigs.${index}.pointsAmount`]" class="invalid-feedback">
                      {{ errors[`tierConfigs.${index}.pointsAmount`] }}
                    </div>
                  </div>
                  <div v-if="tierConfig.sendPoints" class="form-group">
                    <p class="form-text" style="margin-bottom: 0;">
                      贈點到期日依「客戶設定 → 點數過期設定」統一計算，與消費贈點相同。
                    </p>
                  </div>

                  <!-- 優惠券設定 -->
                  <div class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送優惠券</label>
                      <label class="toggle-switch">
                        <input v-model="tierConfig.sendCoupons" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="tierConfig.sendCoupons ? 'on' : 'off'">
                        {{ tierConfig.sendCoupons ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="tierConfig.sendCoupons" class="form-group">
                    <label class="form-label">優惠券模板 <span class="required">*</span></label>
                    <div v-if="templatesLoading" class="loading-templates">
                      <i class="ri-loader-4-line spin"></i>
                      <span>載入優惠券模板中...</span>
                    </div>
                    <div v-else class="template-selector">
                      <div class="template-search">
                        <input v-model="templateSearchQueries[index]" type="text" class="form-control" placeholder="搜尋優惠券模板..." @focus="$event.target.select()" />
                      </div>
                      <div class="template-list">
                        <div
                          v-for="template in getFilteredTemplates(index)"
                          :key="template.id"
                          class="template-item"
                          :class="{ 'selected': isTemplateSelected(template.id, index) }"
                          @click="toggleTemplate(template.id, index)"
                        >
                          <input type="checkbox" :checked="isTemplateSelected(template.id, index)" @change="toggleTemplate(template.id, index)" @click.stop />
                          <div class="template-info">
                            <div class="template-name">{{ template.templateName }}</div>
                            <RewardStockWarning :template="template" />
                            <div class="template-details">
                              <span class="template-type">{{ getCouponTypeText(template.couponType) }}</span>
                              <span v-if="template.discountValue" class="template-discount">{{ formatDiscount(template.discountValue, template.discountUnit) }}</span>
                              <span v-if="template.validityDays" class="template-validity">有效 {{ template.validityDays }} 天</span>
                            </div>
                          </div>
                        </div>
                        <div v-if="getFilteredTemplates(index).length === 0" class="no-templates">
                          <i class="ri-inbox-line"></i>
                          <p>沒有找到優惠券模板</p>
                          <small v-if="templateSearchQueries[index]">請嘗試其他搜尋關鍵字</small>
                        </div>
                      </div>
                    </div>
                    <div v-if="getSelectedTemplates(index).length > 0" class="selected-templates">
                      <div class="selected-label">已選取 {{ getSelectedTemplates(index).length }} 個優惠券模板：</div>
                      <div class="selected-tags">
                        <span v-for="template in getSelectedTemplates(index)" :key="template.id" class="selected-tag">
                          {{ template.templateName }}
                          <button type="button" class="remove-tag" @click="removeTemplate(template.id, index)">
                            <i class="ri-close-line"></i>
                          </button>
                        </span>
                      </div>
                      <RewardStockWarning
                        v-for="template in getSelectedTemplates(index)"
                        :key="`stock-${index}-${template.id}`"
                        :template="template"
                      />
                    </div>
                    <div v-if="errors[`tierConfigs.${index}.couponTemplateIds`]" class="invalid-feedback" style="display:block;">
                      {{ errors[`tierConfigs.${index}.couponTemplateIds`] }}
                    </div>
                    <small class="form-text">可選擇多個優惠券模板，升等禮會發送所有選中的優惠券</small>
                  </div>

                  <div v-if="isWixtarClient" class="form-group">
                    <div class="toggle-row">
                      <label class="toggle-label">發送 Wixtar 票券</label>
                      <label class="toggle-switch">
                        <input v-model="tierConfig.sendWixtarTickets" type="checkbox" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status" :class="tierConfig.sendWixtarTickets ? 'on' : 'off'">
                        {{ tierConfig.sendWixtarTickets ? '已啟用' : '已停用' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="isWixtarClient && tierConfig.sendWixtarTickets" class="form-group">
                    <label class="form-label">Wixtar 票券 <span class="required">*</span></label>
                    <WixtarTicketMultiSelect
                      :model-value="tierConfig.wixtarTicketIds || []"
                      :tickets="wixtarTickets"
                      :loading="wixtarTicketsLoading"
                      @update:model-value="setTierWixtarTicketIds(index, $event)"
                    />
                    <div v-if="errors[`tierConfigs.${index}.wixtarTicketIds`]" class="invalid-feedback" style="display:block;">
                      {{ errors[`tierConfigs.${index}.wixtarTicketIds`] }}
                    </div>
                    <small class="form-text">可選擇多張 Wixtar 票券，升等禮會各發送一張。會員需已綁定 Wixtar。</small>
                  </div>
                </div>
              </div>

              <small class="form-text" style="color:#6c757d;">
                <i class="ri-information-line"></i>
                設定區塊依目標等級自動產生，每個等級可個別設定點數與優惠券內容<span v-if="isWixtarClient">，以及 Wixtar 票券</span>
              </small>
            </div>

          </form>
        </div>
      </div>

      <!-- ======== Tab 3：通知設定 ======== -->
      <div v-show="activeTab === 'notification'" class="card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">
            <div class="form-section">
              <h2 class="section-title">通知設定</h2>
              <NotificationChannelSelector
                v-model:channels="form.notificationChannels"
                v-model:templateIds="form.notificationTemplateIds"
                show-default-template-preview
              />
              <span v-if="errors.notificationChannels" class="error-text" style="display:block;margin-top:8px;">
                {{ errors.notificationChannels }}
              </span>
            </div>
          </form>
        </div>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { autoSendService } from '@/api/modules/autoSend.js';
import { couponService } from '@/api/modules/coupons.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { clientService } from '@/api/modules/clients.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError } from '@/utils/swal.js';
import NotificationChannelSelector from '@/components/NotificationChannelSelector.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';
import RewardStockWarning from '@/components/RewardStockWarning.vue';

export default {
  name: 'TierUpgradeGiftConfig',
  components: { NotificationChannelSelector, WixtarTicketMultiSelect, RewardStockWarning },
  setup() {
    const { pageTitle } = usePageTitle('升等禮設定');
    const loading = ref(false);
    const submitting = ref(false);
    const activeTab = ref('basic');
    
    const form = reactive({
      isEnabled: false,
      tierConfigs: [
        {
          targetTier: '',
          sendPoints: false,
          pointsAmount: 0,
          sendCoupons: false,
          couponTemplateIds: [],
          sendWixtarTickets: false,
          wixtarTicketIds: []
        }
      ],
      onlyFirstUpgrade: true,
      sendNotification: false,
      notificationChannel: null,
      customMessageTemplate: null,
      notificationChannels: [],
      notificationTemplateIds: {}
    });

    const errors = reactive({});
    const templates = ref([]);
    const templatesLoading = ref(false);
    const isWixtarClient = ref(false);
    const memberSystemLoaded = ref(false);
    const wixtarTransactionTierUpgradeEnabled = ref(false);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const templateSearchQueries = ref({});
    const tiers = ref([]);
    const tiersLoading = ref(false);

    // 取得指定 index 可用的會員等級（排除其他 config 已選的等級，保留自己目前選中的等級）
    const getAvailableTiers = (currentIndex) => {
      const selectedByOthers = form.tierConfigs
        .filter((_, i) => i !== currentIndex)
        .map(config => config.targetTier)
        .filter(Boolean);
      return tiers.value.filter(tier => !selectedByOthers.includes(tier.tierCode));
    };

    // 是否還有尚未被任何 config 選取的等級（決定是否能新增等級設定）
    const hasRemainingTiers = computed(() => {
      const allSelected = form.tierConfigs.map(c => c.targetTier).filter(Boolean);
      return tiers.value.some(tier => !allSelected.includes(tier.tierCode));
    });

    // 取得等級名稱
    const getTierName = (tierCode) => {
      if (!tierCode) return '未選擇';
      const tier = tiers.value.find(t => t.tierCode === tierCode);
      return tier ? tier.tierName : tierCode;
    };

    // 過濾優惠券模板（根據搜尋關鍵字和索引）
    const getFilteredTemplates = (index) => {
      const query = templateSearchQueries.value[index] || '';
      if (!query || !query.trim()) {
        return templates.value;
      }
      const searchLower = query.toLowerCase().trim();
      return templates.value.filter(template =>
        template.templateName.toLowerCase().includes(searchLower) ||
        (template.couponType && template.couponType.toLowerCase().includes(searchLower))
      );
    };

    // 已選取的優惠券模板（根據索引）
    const getSelectedTemplates = (index) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig || !tierConfig.couponTemplateIds || tierConfig.couponTemplateIds.length === 0) {
        return [];
      }
      return templates.value.filter(t => tierConfig.couponTemplateIds.includes(t.id));
    };

    // 載入會員等級列表
    const loadTiers = async () => {
      tiersLoading.value = true;
      try {
        const response = await memberTierService.getTiers({
          isActive: true,
          sortBy: 'displayOrder',
          order: 'asc'
        });
        if (response && response.success && response.data) {
          tiers.value = Array.isArray(response.data) ? response.data : (response.data.items || []);
        }
      } catch (error) {
        console.error('載入會員等級失敗:', error);
        showError('載入會員等級失敗', error.message);
      } finally {
        tiersLoading.value = false;
      }
    };

    // 載入優惠券模板列表
    const loadTemplates = async () => {
      templatesLoading.value = true;
      try {
        const response = await couponService.getTemplates({
          pageSize: 100,
          isActive: true
        });
        if (response && response.success && response.data) {
          templates.value = response.data.items || [];
        }
      } catch (error) {
        console.error('載入優惠券模板失敗:', error);
        showError('載入優惠券模板失敗', error.message);
      } finally {
        templatesLoading.value = false;
      }
    };

    // 檢查模板是否已選取
    const isTemplateSelected = (templateId, index) => {
      const tierConfig = form.tierConfigs[index];
      return tierConfig && tierConfig.couponTemplateIds && tierConfig.couponTemplateIds.includes(templateId);
    };

    // 切換模板選取狀態
    const toggleTemplate = (templateId, index) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig.couponTemplateIds) {
        tierConfig.couponTemplateIds = [];
      }
      const templateIndex = tierConfig.couponTemplateIds.indexOf(templateId);
      if (templateIndex > -1) {
        tierConfig.couponTemplateIds.splice(templateIndex, 1);
      } else {
        tierConfig.couponTemplateIds.push(templateId);
      }
      // 如果有選取模板，自動啟用 sendCoupons
      if (tierConfig.couponTemplateIds.length > 0) {
        tierConfig.sendCoupons = true;
      }
    };

    // 移除模板
    const removeTemplate = (templateId, index) => {
      const tierConfig = form.tierConfigs[index];
      if (tierConfig && tierConfig.couponTemplateIds) {
        const templateIndex = tierConfig.couponTemplateIds.indexOf(templateId);
        if (templateIndex > -1) {
          tierConfig.couponTemplateIds.splice(templateIndex, 1);
        }
        // 如果沒有選取模板，自動停用 sendCoupons
        if (tierConfig.couponTemplateIds.length === 0) {
          tierConfig.sendCoupons = false;
        }
      }
    };

    // 取得優惠券類型文字
    const getCouponTypeText = (type) => {
      const typeMap = {
        'discount': '折扣券',
        'fixed_amount': '折價券',
        'free_shipping': '免運券',
        'gift': '贈品券'
      };
      return typeMap[type] || type || '優惠券';
    };

    // 格式化折扣資訊
    const formatDiscount = (value, unit) => {
      if (!value) return '';
      if (unit === 'percent') {
        return `${value}% 折扣`;
      } else if (unit === 'fixed') {
        return `折抵 NT$ ${value}`;
      }
      return `${value} ${unit}`;
    };

    // 依所有等級自動生成 tierConfigs（保留已存在的設定）
    const autoGenerateTierConfigs = () => {
      const existingMap = {};
      form.tierConfigs.forEach(config => {
        if (config.targetTier) existingMap[config.targetTier] = config;
      });
      form.tierConfigs = tiers.value.map((tier, index) => {
        const existing = existingMap[tier.tierCode];
        const config = existing || {
          targetTier: tier.tierCode,
          sendPoints: false,
          pointsAmount: 0,
          sendCoupons: false,
          couponTemplateIds: [],
          sendWixtarTickets: false,
          wixtarTicketIds: []
        };
        if (!existing) config.targetTier = tier.tierCode;
        if (!config.wixtarTicketIds) config.wixtarTicketIds = [];
        if (!templateSearchQueries.value[index]) {
          templateSearchQueries.value[index] = '';
        }
        return config;
      });
    };

    // 新增等級設定
    const addTierConfig = () => {
      if (!hasRemainingTiers.value) {
        showError('無法新增', '沒有可用的會員等級');
        return;
      }
      form.tierConfigs.push({
        targetTier: '',
        sendPoints: false,
        pointsAmount: 0,
        sendCoupons: false,
        couponTemplateIds: [],
        sendWixtarTickets: false,
        wixtarTicketIds: []
      });
      // 初始化搜尋查詢
      const index = form.tierConfigs.length - 1;
      templateSearchQueries.value[index] = '';
    };

    // 移除等級設定
    const removeTierConfig = (index) => {
      if (form.tierConfigs.length > 1) {
        form.tierConfigs.splice(index, 1);
        // 清理對應的搜尋查詢
        delete templateSearchQueries.value[index];
      }
    };

    const parseIdList = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.map((id) => String(id));
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [String(parsed)];
        } catch {
          return [value];
        }
      }
      return [String(value)];
    };

    const setTierWixtarTicketIds = (index, ids) => {
      const tierConfig = form.tierConfigs[index];
      if (!tierConfig) return;
      tierConfig.wixtarTicketIds = ids;
      if (ids.length > 0) tierConfig.sendWixtarTickets = true;
    };

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) {
        isWixtarClient.value = false;
        memberSystemLoaded.value = true;
        return;
      }
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.data?.clientData?.memberSystemType === 'WIXTAR';
      } catch (error) {
        console.error('載入會員模式失敗:', error);
        isWixtarClient.value = false;
        memberSystemLoaded.value = false;
        return;
      }
      memberSystemLoaded.value = true;
      if (isWixtarClient.value) {
        await loadWixtarTierUpgradeSetting();
      }
    };

    const loadWixtarTierUpgradeSetting = async () => {
      try {
        const [wixtarRes, tierRes] = await Promise.all([
          clientSettingsService.get(NAMESPACES.WIXTAR),
          clientSettingsService.get(NAMESPACES.MEMBER_TIER)
        ]);
        const wixtar = wixtarRes?.data?.settings ?? {};
        const tier = tierRes?.data?.settings ?? {};
        wixtarTransactionTierUpgradeEnabled.value = !!(
          wixtar.wixtarTransactionTierUpgradeEnabled || tier.wixtarTransactionTierUpgradeEnabled
        );
      } catch (error) {
        console.error('載入 Wixtar 交易升等設定失敗:', error);
        wixtarTransactionTierUpgradeEnabled.value = false;
      }
    };

    const loadWixtarTickets = async () => {
      if (!isWixtarClient.value) {
        wixtarTickets.value = [];
        return;
      }
      wixtarTicketsLoading.value = true;
      try {
        const all = [];
        let page = 1;
        let totalPages = 1;
        do {
          const response = await wixtarService.getTicketCatalog({
            page,
            pageSize: 100,
            sortBy: 'activity',
            sortOrder: 'asc'
          });
          const items = response?.data?.items || [];
          all.push(...items);
          totalPages = response?.data?.pagination?.totalPages || 1;
          page += 1;
        } while (page <= totalPages && page <= 10);
        wixtarTickets.value = all;
      } catch (error) {
        console.error('載入 Wixtar 票券失敗:', error);
        wixtarTickets.value = [];
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    // 載入設定
    const loadConfig = async () => {
      loading.value = true;
      try {
        const response = await autoSendService.getTierUpgradeGiftConfig();
        
        if (response && response.success && response.data) {
          Object.assign(form, response.data);
          
          // 確保 tierConfigs 是陣列
          if (!form.tierConfigs || !Array.isArray(form.tierConfigs) || form.tierConfigs.length === 0) {
            form.tierConfigs = [{
              targetTier: '',
              sendPoints: false,
              pointsAmount: 0,
              sendCoupons: false,
              couponTemplateIds: [],
              sendWixtarTickets: false,
              wixtarTicketIds: []
            }];
          }
          
          // 處理每個 tierConfig 的 couponTemplateIds
          form.tierConfigs.forEach((config, index) => {
            if (config.couponTemplateIds) {
              if (Array.isArray(config.couponTemplateIds)) {
                // 已經是陣列，直接使用
              } else if (typeof config.couponTemplateIds === 'string') {
                try {
                  const parsed = JSON.parse(config.couponTemplateIds);
                  config.couponTemplateIds = Array.isArray(parsed) ? parsed : [parsed];
                } catch {
                  config.couponTemplateIds = [];
                }
              } else {
                config.couponTemplateIds = [config.couponTemplateIds];
              }
            } else {
              config.couponTemplateIds = [];
            }
            
            // 初始化搜尋查詢
            if (!templateSearchQueries.value[index]) {
              templateSearchQueries.value[index] = '';
            }
            
            // 如果有 couponTemplateIds，自動啟用 sendCoupons
            if (config.couponTemplateIds && config.couponTemplateIds.length > 0) {
              config.sendCoupons = true;
            }

            config.wixtarTicketIds = parseIdList(config.wixtarTicketIds);
            if (config.wixtarTicketIds.length > 0) {
              config.sendWixtarTickets = true;
            } else if (config.sendWixtarTickets == null) {
              config.sendWixtarTickets = false;
            }
          });

          // 新版多管道通知設定
          form.notificationChannels = Array.isArray(response.data.notificationChannels)
            ? response.data.notificationChannels : [];
          form.notificationTemplateIds = response.data.notificationTemplateIds || {};

          // 自動補齊所有等級的設定區塊
          autoGenerateTierConfigs();
        }
      } catch (error) {
        console.error('載入設定失敗:', error);
        showError('載入設定失敗', error.message);
      } finally {
        loading.value = false;
      }
    };

    // 驗證表單
    const validateForm = () => {
      // 清除所有錯誤
      Object.keys(errors).forEach(key => delete errors[key]);

      // 驗證每個 tierConfig
      let isValid = true;
      form.tierConfigs.forEach((config, index) => {
        if (!config.targetTier) {
          errors[`tierConfigs.${index}.targetTier`] = '請選擇目標等級';
          isValid = false;
        }
        
        // 檢查是否有重複的目標等級
        const duplicateIndex = form.tierConfigs.findIndex((c, i) => 
          i !== index && c.targetTier === config.targetTier && config.targetTier
        );
        if (duplicateIndex !== -1) {
          errors[`tierConfigs.${index}.targetTier`] = '目標等級不能重複';
          isValid = false;
        }

        // 驗證點數設定
        if (config.sendPoints) {
          if (!config.pointsAmount || config.pointsAmount <= 0) {
            errors[`tierConfigs.${index}.pointsAmount`] = '點數數量必須大於 0';
            isValid = false;
          }
        }

        // 驗證優惠券設定
        if (config.sendCoupons) {
          if (!config.couponTemplateIds || config.couponTemplateIds.length === 0) {
            errors[`tierConfigs.${index}.couponTemplateIds`] = '請至少選擇一個優惠券模板';
            isValid = false;
          }
        }
        if (isWixtarClient.value && config.sendWixtarTickets && (!config.wixtarTicketIds || config.wixtarTicketIds.length === 0)) {
          errors[`tierConfigs.${index}.wixtarTicketIds`] = '請至少選擇一張 Wixtar 票券';
          isValid = false;
        }
      });

      // 未選擇自訂範本時，發送端會使用系統預設範本
      errors.notificationChannels = '';

      return isValid;
    };

    // 提交表單
    const handleSubmit = async () => {
      if (!validateForm()) {
        const firstError = Object.values(errors).find(v => v);
        showError('驗證失敗', firstError || '請檢查輸入資料');
        return;
      }

      submitting.value = true;
      try {
        // 準備提交資料
        const tierConfigs = form.tierConfigs.map(config => ({
          targetTier: config.targetTier,
          sendPoints: config.sendPoints || false,
          pointsAmount: config.sendPoints ? (config.pointsAmount || 0) : null,
          pointsExpiryDays: null,
          sendCoupons: config.sendCoupons || false,
          couponTemplateIds: config.sendCoupons && config.couponTemplateIds && config.couponTemplateIds.length > 0
            ? config.couponTemplateIds
            : [],
          sendWixtarTickets: memberSystemLoaded.value && !isWixtarClient.value
            ? false
            : (config.sendWixtarTickets || false),
          wixtarTicketIds: memberSystemLoaded.value && !isWixtarClient.value
            ? []
            : (config.sendWixtarTickets && config.wixtarTicketIds && config.wixtarTicketIds.length > 0
              ? config.wixtarTicketIds
              : [])
        }));

        const data = {
          isEnabled: form.isEnabled,
          tierConfigs,
          onlyFirstUpgrade: form.onlyFirstUpgrade,
          sendNotification: form.notificationChannels && form.notificationChannels.length > 0,
          notificationChannel: form.notificationChannels && form.notificationChannels.length > 0
            ? form.notificationChannels[0] : null,
          customMessageTemplate: null,
          notificationChannels: form.notificationChannels || [],
          notificationTemplateIds: form.notificationTemplateIds || {}
        };

        const response = await autoSendService.updateTierUpgradeGiftConfig(data);

        if (response && response.success) {
          await showSuccess('設定儲存成功', '升等禮設定已更新');
          await loadConfig(); // 重新載入以取得最新資料
        } else {
          showError('儲存設定失敗', response?.message || '未知錯誤');
        }
      } catch (error) {
        showError('儲存設定失敗', error.message || '請檢查輸入資料');
      } finally {
        submitting.value = false;
      }
    };

    // 重置表單
    const handleReset = () => {
      loadConfig();
    };

    onMounted(async () => {
      // 必須先載入等級清單，再載入設定
      // 否則 loadConfig 先完成時 tiers 仍為空，<select> 找不到對應 option，瀏覽器會清空選取值
      await loadClientMemberSystem();
      await Promise.all([loadTiers(), loadTemplates(), loadWixtarTickets()]);
      await loadConfig();
    });

    return {
      pageTitle,
      loading,
      activeTab,
      form,
      errors,
      submitting,
      templates,
      templatesLoading,
      templateSearchQueries,
      tiers,
      getAvailableTiers,
      hasRemainingTiers,
      getTierName,
      getFilteredTemplates,
      getSelectedTemplates,
      isTemplateSelected,
      toggleTemplate,
      removeTemplate,
      getCouponTypeText,
      formatDiscount,
      addTierConfig,
      removeTierConfig,
      handleSubmit,
      handleReset,
      isWixtarClient,
      wixtarTransactionTierUpgradeEnabled,
      wixtarTickets,
      wixtarTicketsLoading,
      setTierWixtarTicketIds
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* ===== Toggle 開關 ===== */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on { color: #4caf50; }
.toggle-status.off { color: #9ca3af; }

/* ===== Toggle 開關 ===== */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on { color: #4caf50; }
.toggle-status.off { color: #9ca3af; }

/* ===== Tab 導航 ===== */
.tab-nav {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.tab-btn {
  padding: 10px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.tab-btn:hover {
  color: #1976d2;
}

.tab-btn.active {
  color: #1976d2;
  border-bottom-color: #1976d2;
}

.form-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.wixtar-tier-upgrade-notice {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 12px 14px;
  background: #fff8e6;
  border: 1px solid #f0d78c;
  border-radius: 8px;
  color: #7a5b00;
  font-size: 13px;
  line-height: 1.55;
}

.wixtar-tier-upgrade-notice > i {
  flex-shrink: 0;
  margin-top: 2px;
  color: #c98a00;
  font-size: 18px;
}

.wixtar-tier-upgrade-notice p {
  margin: 0 0 6px;
}

.wixtar-tier-upgrade-notice p:last-child {
  margin-bottom: 0;
}

.wixtar-tier-upgrade-notice a {
  color: #5746e3;
  font-weight: 600;
}

.wixtar-tier-upgrade-notice .notice-on {
  color: #1b5e20;
}

.wixtar-tier-upgrade-notice .notice-off {
  color: #8a5a00;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.required {
  color: #dc3545;
}

.error-text {
  display: block;
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.form-text {
  display: block;
  margin-top: 5px;
  color: #6c757d;
  font-size: 14px;
}
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 等級設定卡片 */
.tier-config-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 20px;
  background: #fff;
}

.tier-config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
}

.tier-config-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #333;
}

.tier-config-body {
  padding: 20px;
}

/* 優惠券模板選擇器樣式 */
.loading-templates {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px;
  text-align: center;
  color: #666;
}

.template-selector {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
}

.template-search {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.template-search input {
  width: 100%;
}

.template-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
}

.template-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.template-item:hover {
  background: #f5f5f5;
  border-color: #1976d2;
}

.template-item.selected {
  background: #e3f2fd;
  border-color: #1976d2;
}

.template-item input[type="checkbox"] {
  margin-top: 2px;
  cursor: pointer;
}

.template-info {
  flex: 1;
}

.template-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.template-details {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #666;
}

.template-type {
  padding: 2px 8px;
  background: #f0f0f0;
  border-radius: 12px;
}

.template-discount {
  color: #1976d2;
  font-weight: 500;
}

.template-validity {
  color: #666;
}

.no-templates {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-templates i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.selected-templates {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.selected-label {
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #1976d2;
  color: white;
  border-radius: 16px;
  font-size: 14px;
}

.remove-tag {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 1;
}

.remove-tag:hover {
  opacity: 0.8;
}
</style>
